import Config from '../Config.jsx';

// Content kinds that only PRESENT something — a student never answers them, so
// they are not part of the "Q: answered/total" count. Mirrors
// CreateContentDialog.isPresentationContentType; everything else is a question.
const PRESENTATION_CONTENT_KINDS = [
  Config.HOTSPOT_CONTENT_SIMPLE_DESCRIPTION,
  Config.HOTSPOT_CONTENT_VOICE_OVER,
  Config.HOTSPOT_CONTENT_MOVIECLIP,
  Config.HOTSPOT_CONTENT_3D_MODEL,
];

// Kinds the backend will actually send to the model, and only when the
// component carries a marking scheme. Mirrors get_all_ai_assessment_response.
const AI_ASSESSMENT_KINDS = new Set([
  Config.HOTSPOT_CONTENT_QUESTION,
  Config.HOTSPOT_CONTENT_FILL_IN_THE_BLANK,
  Config.HOTSPOT_CONTENT_TAKE_RECORD,
]);

const MARKING_SCHEME_KEYS = ['correctAnswer', 'answerToExclude', 'otherGuidelines'];

export function isQuestionKind(kind) {
  const parsed = parseInt(kind, 10);
  return !isNaN(parsed) && parsed > 0 && !PRESENTATION_CONTENT_KINDS.includes(parsed);
}

function parsePayload(component) {
  const raw = component && component.payload;
  if (!raw) return null;
  if (typeof raw === 'object') return raw;
  try {
    return JSON.parse(String(raw).replace(/[\n\r]/g, ''));
  } catch (e) {
    return null;
  }
}

function hasMarkingScheme(payload) {
  if (!payload || typeof payload !== 'object') return false;
  return MARKING_SCHEME_KEYS.some((key) => {
    const value = payload[key];
    return value !== undefined && value !== null && String(value).trim() !== '';
  });
}

/** A question the AI pipeline can grade. Others never receive an AI percentage. */
export function isAiAssessableQuestion(component) {
  if (!component) return false;
  const kind = parseInt(component.kind, 10);
  return AI_ASSESSMENT_KINDS.has(kind) && hasMarkingScheme(parsePayload(component));
}

export function extractAiScoreFromAssessment(assessmentText) {
  if (!assessmentText) return null;
  const text = String(assessmentText).trim();
  if (text === 'No answer provided' || text === '') return 0;

  const percentageMatch = text.match(/<(\d+)%?>|<(\d+)％>|正確程度：<(\d+)[%％]>|正確程度評分：<(\d+)[%％]>/);
  if (percentageMatch) {
    const score = parseInt(
      percentageMatch[1] || percentageMatch[2] || percentageMatch[3] || percentageMatch[4],
      10
    );
    return isNaN(score) ? null : score;
  }

  const scoreMatch = text.match(/Score:\s*(\d+)[%％]|正確程度[:：]\s*(\d+)[%％]/i);
  if (scoreMatch) {
    const score = parseInt(scoreMatch[1] || scoreMatch[2], 10);
    return isNaN(score) ? null : score;
  }

  return null;
}

function round1(value) {
  return Math.round(value * 10) / 10;
}

function plainQuestionText(value) {
  return String(value || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
}

function questionLabel(fullText) {
  if (!fullText) return '';
  return fullText.length > 50 ? `${fullText.slice(0, 50)}...` : fullText;
}

/**
 * Everything the card's counters and their tooltips need out of one
 * submission's answer tree: totals, what the student answered, and the
 * per-hotspot breakdown the tooltips list.
 *
 * Two AI percentages come out of the same walk:
 *   aiPercent     — mean of the questions the student actually answered.
 *                   An unanswered question is an absence, not a 0%.
 *   aiPercentAll  — the same mean over every AI-assessable question.
 *                   One the student did not answer counts as 0%. A question
 *                   they did answer, but which has no parseable assessment
 *                   yet, is left out: it is not a zero, the mark is not in.
 * Both stay null until this submission has at least one stored assessment,
 * so a locale that was never sent to the model does not show a wall of 0%.
 */
export function summariseSubmissionAnswers(hotspotList) {
  const kindByComponentId = {};
  (Array.isArray(hotspotList) ? hotspotList : []).forEach((hotspot) => {
    const detail = hotspot && hotspot.hotspot_detail;
    const components = (detail && Array.isArray(detail.component_list)) ? detail.component_list : [];
    components.forEach((component) => {
      if (component) {
        kindByComponentId[String(component.id)] = component.kind;
      }
    });
  });

  const breakdown = [];
  let totalQuestions = 0;
  let answeredQuestions = 0;
  let visitedHotspots = 0;
  let aiSum = 0;
  let aiCount = 0;
  let aiAllSum = 0;
  let aiAllCount = 0;
  let hasAnyAiAssessment = false;
  let earnedMarksTotal = 0;
  let maxMarksTotal = 0;
  let earnedAnsweredTotal = 0;
  let maxAnsweredTotal = 0;
  const questions = [];

  (Array.isArray(hotspotList) ? hotspotList : []).forEach((hotspot) => {
    const detail = hotspot && hotspot.hotspot_detail;
    const components = (detail && Array.isArray(detail.component_list)) ? detail.component_list : [];
    const questionIds = new Set();
    const markedIds = new Set();
    const maxByComponentId = {};
    let maxMarks = 0;
    const hotspotEligibleIds = new Set();
    const contentById = {};
    components.forEach((component) => {
      if (!component) return;
      contentById[String(component.id)] = plainQuestionText(component.content);
      if (isQuestionKind(component.kind)) {
        questionIds.add(String(component.id));
        const componentMax = parseFloat(component.score_max);
        if (componentMax >= 0) {
          markedIds.add(String(component.id));
          maxMarks += componentMax;
          maxByComponentId[String(component.id)] = componentMax;
        }
      }
      if (isAiAssessableQuestion(component)) {
        hotspotEligibleIds.add(String(component.id));
      }
    });

    const answeredIds = new Set();
    const hotspotAnsweredForAi = new Set();
    const scoresByComponent = {};
    const scoredEarnedByComponent = {};
    const answeredMarkedIds = new Set();
    let earnedMarks = 0;
    let hotspotAiSum = 0;
    let hotspotAiCount = 0;
    const answers = (hotspot && Array.isArray(hotspot.answer_list)) ? hotspot.answer_list : [];
    answers.forEach((answer) => {
      if (!answer) return;
      // answer.score defaults to -1 ("unscored"); the backend's SUM ignores
      // those, so the tip's per-hotspot marks have to ignore them too or the
      // rows would not add up to the (x/y) above them.
      const answerScore = parseFloat(answer.score);
      if (answerScore >= 0) {
        earnedMarks += answerScore;
      }

      const componentId = String(answer.component_id);
      const kind = (kindByComponentId[componentId] !== undefined) ? kindByComponentId[componentId] : answer.kind;
      // A voice answer has no `answer` text but does have a transcription, so
      // both count as having been answered. A blank row means the student
      // opened the question and left it empty.
      const hasAnswerText = !(answer.answer === undefined || answer.answer === null
        || String(answer.answer).trim() === '');
      const hasTranscription = !(answer.voice_to_text_transcription === undefined
        || answer.voice_to_text_transcription === null
        || String(answer.voice_to_text_transcription).trim() === '');
      const assessmentText = answer.ai_assessment_result;
      const hasAssessment = !(assessmentText === undefined || assessmentText === null
        || String(assessmentText).trim() === '');

      // A recorded mark belongs to the all-questions total either way. It
      // belongs to the answered total only when the student actually answered
      // and the question carries marks — a blank that was stored as 0, or a
      // question they never opened, stays out of that denominator.
      if (answerScore >= 0 && maxByComponentId[componentId] !== undefined) {
        scoredEarnedByComponent[componentId] = (scoredEarnedByComponent[componentId] || 0) + answerScore;
      }
      if ((hasAnswerText || hasTranscription) && maxByComponentId[componentId] !== undefined) {
        answeredMarkedIds.add(componentId);
      }

      if (hasAssessment && isQuestionKind(kind)) {
        hasAnyAiAssessment = true;
        hotspotEligibleIds.add(componentId);
      }

      if (hasAnswerText || hasTranscription) {
        hotspotAnsweredForAi.add(componentId);
        const answerAiPercent = extractAiScoreFromAssessment(assessmentText);
        if (answerAiPercent !== null && !isNaN(answerAiPercent)) {
          aiSum += answerAiPercent;
          aiCount += 1;
          hotspotAiSum += answerAiPercent;
          hotspotAiCount += 1;
          if (!scoresByComponent[componentId]) scoresByComponent[componentId] = [];
          scoresByComponent[componentId].push(answerAiPercent);
          if (isQuestionKind(kind)) hotspotEligibleIds.add(componentId);
        }
      }

      if (!hasAnswerText) {
        return;
      }
      if (isQuestionKind(kind)) {
        answeredIds.add(componentId);
      }
    });

    // One slot per question. Several answer rows for the same component
    // collapse to their mean, so a question the student took twice still
    // counts once. An answered question with no parseable score is waiting
    // on the model and is skipped; an unanswered one is a 0.
    let hotspotAllSum = 0;
    let hotspotAllCount = 0;
    hotspotEligibleIds.forEach((id) => {
      const scores = scoresByComponent[id];
      if (scores && scores.length) {
        hotspotAllSum += scores.reduce((sum, score) => sum + score, 0) / scores.length;
        hotspotAllCount += 1;
      } else if (!hotspotAnsweredForAi.has(id)) {
        hotspotAllSum += 0;
        hotspotAllCount += 1;
      }
    });
    aiAllSum += hotspotAllSum;
    aiAllCount += hotspotAllCount;

    let earnedAnswered = 0;
    let maxAnswered = 0;
    answeredMarkedIds.forEach((id) => {
      if (scoredEarnedByComponent[id] === undefined) return;
      earnedAnswered += scoredEarnedByComponent[id];
      maxAnswered += maxByComponentId[id];
    });

    // One row per AI question, so the class lists can average the same slots
    // the card does: a parsed score, a 0 for a skip, or nothing while the
    // model still owes an answered question its assessment.
    hotspotEligibleIds.forEach((id) => {
      const scores = scoresByComponent[id];
      let aiAnswered = null;
      let aiAll = null;
      if (scores && scores.length) {
        const mean = scores.reduce((sum, score) => sum + score, 0) / scores.length;
        aiAnswered = mean;
        aiAll = mean;
      } else if (!hotspotAnsweredForAi.has(id)) {
        aiAll = 0;
      }
      const fullText = contentById[id] || '';
      questions.push({
        id,
        text: questionLabel(fullText),
        fullText,
        aiAnswered,
        aiAll,
      });
    });

    earnedMarksTotal += earnedMarks;
    maxMarksTotal += maxMarks;
    earnedAnsweredTotal += earnedAnswered;
    maxAnsweredTotal += maxAnswered;

    totalQuestions += questionIds.size;
    answeredQuestions += answeredIds.size;
    if (answeredIds.size > 0) {
      visitedHotspots += 1;
    }
    breakdown.push({
      id: (detail && detail.id !== undefined && detail.id !== null) ? detail.id : null,
      name: (detail && detail.name) ? detail.name : '',
      answered: answeredIds.size,
      marked: markedIds.size,
      total: questionIds.size,
      earned: earnedMarks,
      max: maxMarks,
      earnedAnswered,
      maxAnswered,
      aiPercent: hotspotAiCount > 0 ? round1(hotspotAiSum / hotspotAiCount) : null,
      aiAllSum: hotspotAllSum,
      aiAllCount: hotspotAllCount,
    });
  });

  const aiPercentAll = (hasAnyAiAssessment && aiAllCount > 0) ? round1(aiAllSum / aiAllCount) : null;
  breakdown.forEach((row) => {
    row.aiPercentAll = (hasAnyAiAssessment && row.aiAllCount > 0)
      ? round1(row.aiAllSum / row.aiAllCount)
      : null;
    delete row.aiAllSum;
    delete row.aiAllCount;
  });

  return {
    totalQuestions,
    answeredQuestions,
    visitedHotspots,
    breakdown,
    aiPercent: aiCount > 0 ? round1(aiSum / aiCount) : null,
    aiPercentAll,
    aiAllQuestionCount: (hasAnyAiAssessment && aiAllCount > 0) ? aiAllCount : 0,
    questions,
    earnedMarks: earnedMarksTotal,
    maxMarks: maxMarksTotal,
    earnedMarksAnswered: earnedAnsweredTotal,
    maxMarksAnswered: maxAnsweredTotal,
  };
}
